import React, { createContext, useContext, useMemo } from 'react';
import { calendarApi } from './calendarApi';

/**
 * What the calendar screens need from whichever app is rendering them.
 *
 * =============================================================================
 * WHY THIS EXISTS
 * =============================================================================
 * CalendarHome, CalendarState, CalendarCounty, CalendarMonth and ReportProblem
 * are now rendered in two places: the subscriber app at
 * calendar.yourfirstparcel.com, and the tenant admin at /admin/calendar.
 *
 * The screens are identical. Two things about their surroundings are not:
 *
 *   WHERE THE DATA COMES FROM. The subscriber app calls /api/calendar with a
 *   token signed by CALENDAR_JWT_SECRET. The tenant admin calls
 *   /api/admin/calendar with an ordinary admin token. Different base path,
 *   different credential, different failure handling on a 401.
 *
 *   WHERE THE LINKS GO. The subscriber app owns its whole router, so a county
 *   link is /counties/12. The tenant admin is one route inside a much larger
 *   app, so the same link has to be /admin/calendar/counties/12.
 *
 * Both were previously hardcoded into each screen, which is fine for one app
 * and impossible for two.
 *
 * =============================================================================
 * WHY A CONTEXT AND NOT PROPS
 * =============================================================================
 * Because ReportProblem is the problem. It is rendered at the bottom of all
 * four screens and needs the api function, so props would mean threading the
 * same two values through every screen purely to hand them to a child that
 * every screen happens to contain. Nothing between the shell and ReportProblem
 * has any use for them.
 *
 * =============================================================================
 * THE DEFAULT IS THE SUBSCRIBER APP
 * =============================================================================
 * A screen rendered with no provider above it behaves exactly as it did before
 * this file existed: the subscriber api, and link paths with no prefix. That is
 * deliberate, so that this refactor cannot change subscriber behaviour by
 * omission, and so the provider is a thing the tenant admin adds rather than a
 * thing the subscriber app must remember not to forget.
 */

const DEFAULT_VALUE = Object.freeze({
  api: calendarApi,
  basePath: '',
  /**
   * Whether the reader may file a problem report.
   *
   * True in both apps today. It is a value rather than an assumption because
   * the report button is the only non-read action on these screens, and a
   * future surface that renders the calendar without one should be able to say
   * so without editing four files.
   */
  canReport: true
});

const CalendarDataContext = createContext(DEFAULT_VALUE);

/**
 * The api function, the link prefix, and a helper that applies it.
 *
 * `path` takes a route as the subscriber app spells it and returns it as the
 * current app spells it. Screens call it instead of writing template literals,
 * so there is one place a prefix can be got wrong rather than eleven.
 */
export function useCalendarData() {
  const value = useContext(CalendarDataContext);

  return useMemo(() => ({
    ...value,
    path: (to) => {
      const suffix = String(to || '');
      if (!value.basePath) return suffix || '/';
      // The home route is the base path itself, not the base path with a
      // trailing slash. React Router treats "/admin/calendar" and
      // "/admin/calendar/" as the same route, but the trailing slash shows up
      // in the address bar and in the active-link comparison, which is how a
      // nav item stops highlighting the page it is on.
      if (suffix === '/' || suffix === '') return value.basePath;
      return `${value.basePath}${suffix}`;
    }
  }), [value]);
}

/**
 * @param {Function} api        (path, options) => Promise, same contract as
 *                              calendarApi: resolves to parsed JSON, throws an
 *                              Error carrying the server's own message.
 * @param {string}   basePath   prefix for every in-app link, no trailing slash
 * @param {boolean}  canReport  whether to render the report button
 */
export function CalendarDataProvider({ api, basePath = '', canReport = true, children }) {
  const value = useMemo(() => ({
    api: api || calendarApi,
    basePath: String(basePath || '').replace(/\/+$/, ''),
    canReport
  }), [api, basePath, canReport]);

  return (
    <CalendarDataContext.Provider value={value}>
      {children}
    </CalendarDataContext.Provider>
  );
}

export default CalendarDataContext;
